import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";

export type CmsButton = {
  label: string;
  href: string;
  variant?: "primary" | "secondary" | "outline" | "gold";
  openInNewTab?: boolean;
};

export type CmsCard = {
  title: string;
  text: string;
  href?: string;
  imageUrl?: string;
  imageAlt?: string;
  imageStoragePath?: string;
};

export type CmsBlockStyle = {
  fontFamily?: "" | "serif" | "sans" | "system" | "georgia" | "times" | "arial" | "verdana" | "trebuchet";
  eyebrowColor?: string;
  headingColor?: string;
  bodyColor?: string;
  backgroundColor?: string;
  sectionHeight?: "" | "compact" | "standard" | "spacious" | "feature" | "full";
  paddingTop?: "" | "tight" | "standard" | "loose";
  paddingBottom?: "" | "tight" | "standard" | "loose";
  contentWidth?: "" | "narrow" | "standard" | "wide";
};

export type CmsBlock = {
  id: string;
  type: "hero" | "text" | "cards" | "steps" | "links" | "cta" | "newsEvents" | "campaignDashboard" | "video" | "imageText" | "gallery" | "slideshow";
  eyebrow?: string;
  title: string;
  body?: string;
  campaignId?: string;
  className?: "band-warm" | "band-green" | "band-ink" | "";
  buttons?: CmsButton[];
  cards?: CmsCard[];
  videoUrl?: string;
  videoStoragePath?: string;
  videoCaption?: string;
  videoDisplay?: "standard" | "playerCard";
  videoAutoplayMuted?: boolean;
  imageUrl?: string;
  imageAlt?: string;
  imageStoragePath?: string;
  imageCaption?: string;
  style?: CmsBlockStyle;
};

export type CmsPage = {
  id: string;
  slug: string;
  title: string;
  seoTitle?: string;
  seoDescription?: string;
  heroImageUrl?: string;
  heroImageAlt?: string;
  heroImageStoragePath?: string;
  heroVideoUrl?: string;
  heroVideoStoragePath?: string;
  status: "published" | "draft";
  blocks: CmsBlock[];
};

export type PublicSiteTheme = {
  primaryColor: string;
  accentColor: string;
  inkColor: string;
  warmBackground: string;
  buttonStyle: "classic" | "soft";
  heroImageUrl?: string;
};

export type PublicChromeConfig = {
  logoUrl: string;
  logoStoragePath?: string;
  footerSummary: string;
  footerBottom: string;
  facebookUrl?: string;
  youtubeUrl?: string;
  showFacebookLink?: boolean;
  showYoutubeLink?: boolean;
  donationUrl?: string;
  parentPortalUrl?: string;
  applyUrl?: string;
  utilityBarBackground?: string;
  headerBackground?: string;
  footerBackground?: string;
};

export type PublicNewsletter = {
  id: string;
  title: string;
  summary: string;
  publishedAt?: string;
  isPublished: boolean;
  attachmentName?: string;
  attachmentUrl?: string;
  attachmentStoragePath?: string;
};

const defaultTheme: PublicSiteTheme = {
  primaryColor: "#376653",
  accentColor: "#c8942e",
  inkColor: "#16213a",
  warmBackground: "#fbf7ef",
  buttonStyle: "classic",
};

const defaultChrome: PublicChromeConfig = {
  logoUrl: "/images/zion-classical-academy-logo.png",
  footerSummary: "Classical Christian education for students from Pre-K through 6th grade in Hastings, Nebraska.",
  footerBottom: "Zion Classical Academy. All rights reserved.",
  facebookUrl: "",
  youtubeUrl: "",
  showFacebookLink: true,
  showYoutubeLink: true,
  donationUrl: "",
  parentPortalUrl: "",
  applyUrl: "",
  utilityBarBackground: "",
  headerBackground: "",
  footerBackground: "",
};

const pageCache = new Map<string, CmsPage | null>();
let themeCache: PublicSiteTheme | null = null;
let chromeCache: PublicChromeConfig | null = null;

function slugToDocId(slug: string) {
  return slug.replace(/\//g, "__");
}

export function getDefaultTheme() {
  return defaultTheme;
}

export function getDefaultChrome() {
  return defaultChrome;
}

export function getCachedPublicTheme() {
  return themeCache;
}

export function getCachedPublicChrome() {
  return chromeCache;
}

export function getCachedCmsPage(slug: string) {
  return pageCache.get(slug) ?? null;
}

export const fallbackPages: Record<string, CmsPage> = {
  home: {
    id: "home",
    slug: "home",
    title: "Home",
    status: "published",
    blocks: [
      {
        id: "hero",
        type: "hero",
        eyebrow: "Zion Classical Academy",
        title: "Classical Christian Education in Hastings, Nebraska",
        body: "Cultivating Truth, Wisdom, and Virtue in students from Pre-K through 6th grade.",
        buttons: [
          { label: "Schedule a Visit", href: "/contact", variant: "gold" },
          { label: "Explore Academics", href: "/academics", variant: "secondary" },
          { label: "Donate/Give", href: "/support", variant: "outline" },
        ],
      },
      {
        id: "mission",
        type: "text",
        eyebrow: "Mission",
        title: "Forming students in truth, wisdom, and virtue",
        body: "Zion Classical Academy partners with families to educate children through a Christ-centered classical model that shapes the mind, heart, and habits.\n\nHastings' first classical Christian school serves students of all denominations from pre-kindergarten through 6th grade.",
      },
      {
        id: "why",
        type: "cards",
        eyebrow: "Classical Christian Education",
        title: "Why this model matters",
        className: "band-warm",
        cards: [
          { title: "Truth", text: "Students learn Scripture, history, literature, science, mathematics, music, and art in a Christ-centered curriculum." },
          { title: "Wisdom", text: "Memory, narration, songs, chants, discussion, and practice help students grow from knowledge toward understanding." },
          { title: "Virtue", text: "Daily habits, character education, family partnership, and school culture support love of God and neighbor." },
        ],
      },
      {
        id: "academics",
        type: "cards",
        eyebrow: "Academics",
        title: "A clear pathway from Pre-K through 6th grade",
        cards: [
          { title: "Preschool", text: "All-day classically inspired preschool with early phonics, numeracy, Scripture, music, and nature exploration.", href: "/academics/preschool" },
          { title: "Kindergarten", text: "Religion, handwriting, math, reading, phonics, language arts, history, science, art, P.E., and music.", href: "/academics/kindergarten" },
          { title: "Elementary", text: "Literature, history, geography, science, Latin, logic, music, art, and P.E.", href: "/academics/elementary" },
        ],
      },
      {
        id: "campus-life",
        type: "cards",
        eyebrow: "Campus Life",
        title: "A school day shaped by wonder, worship, and joyful work",
        className: "band-warm",
        cards: [
          { title: "Chapel and prayer", text: "Students are formed by Scripture, hymns, prayer, and a school culture rooted in Christ." },
          { title: "Songs, chants, and memory", text: "Young learners delight in language, number, history, and faith through repeated practice." },
          { title: "Field trips and community", text: "Families, teachers, churches, and community partners help students see learning as service." },
        ],
      },
      {
        id: "trust",
        type: "cards",
        eyebrow: "Why Families Choose ZCA",
        title: "Warm classrooms, serious academics, and family partnership",
        cards: [
          { title: "Known by name", text: "Small-school relationships help teachers know students and families personally." },
          { title: "Rooted in faith", text: "ZCA welcomes families into a school culture where Christian formation is not separate from learning." },
          { title: "Built for foundations", text: "Pre-K through 6th grade is a formative season for habits, attention, language, and love of learning." },
        ],
      },
      {
        id: "admissions",
        type: "steps",
        eyebrow: "Admissions",
        title: "Visit, apply, and join the ZCA community",
        className: "band-ink",
        cards: [
          { title: "Schedule a Visit", text: "Tour classrooms, meet school leaders, and ask practical questions." },
          { title: "Apply", text: "Submit family information and student records through the admissions process." },
          { title: "Enroll", text: "Complete enrollment steps and prepare for a joyful school year." },
        ],
      },
      { id: "news", type: "newsEvents", eyebrow: "News & Events", title: "School life at a glance" },
      {
        id: "parents",
        type: "links",
        eyebrow: "Parents",
        title: "Quick links for current families",
        className: "band-green",
        cards: [
          { title: "Parent Portal", text: "", href: "/parents" },
          { title: "School Calendar", text: "", href: "/calendar" },
          { title: "Lunch Menu", text: "", href: "/parents" },
          { title: "Forms & Handbooks", text: "", href: "/parents" },
        ],
      },
      {
        id: "support",
        type: "cta",
        eyebrow: "Support ZCA",
        title: "Give toward the formation of the next generation.",
        body: "Your generosity helps Zion Classical Academy serve families with faithful, excellent classical Christian education.",
        buttons: [{ label: "Donate/Give", href: "/support", variant: "gold" }],
      },
    ],
  },
  about: {
    id: "about",
    slug: "about",
    title: "About",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "About ZCA", title: "A school built for family partnership", body: "Zion Classical Academy is a non-profit 501(c)(3) educational organization serving Hastings-area families through classical Christian education.", className: "band-warm" },
      { id: "purpose", type: "text", title: "Our purpose", body: "ZCA exists to nurture loving, serving, and thinking children through a high-quality liberal arts education.\n\nThe school was incorporated in Nebraska in January 2020. Its name reflects biblical roots, a classical model formally adopted in 2019, and high standards of academic excellence." },
      { id: "name", type: "cards", title: "A school name with meaning", className: "band-warm", cards: [{ title: "Zion", text: "A biblical image of God's dwelling place and a tribute to the school's roots." }, { title: "Classical", text: "Ordered learning, rich content, and formation in wisdom." }, { title: "Academy", text: "Academic seriousness, joyful challenge, and educational excellence." }] },
      { id: "partners", type: "text", title: "Ministry partnership", body: "Zion Classical Academy is a Recognized Service Organization of the Lutheran Church-Missouri Synod.\n\nCurrent ministry partners include Zion Lutheran Church in Hastings, Zion Wanda Lutheran Church in Juniata, Heartland Lutheran High School in Grand Island, Peace Lutheran Church in Hastings, and Grace Life Church in Hastings." },
    ],
  },
  "classical-education": {
    id: "classical-education",
    slug: "classical-education",
    title: "Classical Education",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Classical Christian Education", title: "A time-tested model for formation", body: "Classical Christian education invites students into truth, goodness, and beauty through Scripture, rich language, ordered habits, careful thinking, and joyful practice.", className: "band-warm" },
      { id: "trivium", type: "cards", title: "Grammar, logic, and rhetoric", cards: [{ title: "Grammar", text: "Young students build memory, language, number sense, and foundational knowledge." }, { title: "Logic", text: "Students grow in reasoning, comparison, categories, and ordered thought." }, { title: "Rhetoric", text: "Students learn to communicate beautifully and persuasively in service of truth." }] },
      { id: "christ", type: "text", title: "Centered in Christ", body: "ZCA teaches every subject under the lordship of Christ, helping students see the unity of knowledge and the call to love God and neighbor." },
    ],
  },
  academics: {
    id: "academics",
    slug: "academics",
    title: "Academics",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Academics", title: "A rich academic path for young learners", body: "ZCA serves students from Pre-K through 6th grade with a classical Christian curriculum that emphasizes foundational skills, joyful habits, and meaningful content.", className: "band-warm" },
      { id: "levels", type: "cards", title: "Academic levels", cards: [{ title: "Preschool", text: "All-day preschool with early literacy, numeracy, Scripture, music, and nature exploration.", href: "/academics/preschool" }, { title: "Kindergarten", text: "A full elementary foundation in faith, reading, math, language, history, science, art, music, and P.E.", href: "/academics/kindergarten" }, { title: "Elementary", text: "A deepening course of literature, history, geography, science, Latin, logic, music, art, and P.E.", href: "/academics/elementary" }] },
    ],
  },
  "academics/preschool": {
    id: "academics-preschool",
    slug: "academics/preschool",
    title: "Preschool",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Academics", title: "Preschool", body: "Preschool introduces young children to joyful routines, early language, number sense, Scripture, songs, stories, nature, and friendship.", className: "band-warm" },
      { id: "focus", type: "cards", title: "Preschool focus", cards: [{ title: "Early phonics", text: "Students begin hearing, naming, and playing with sounds and letters." }, { title: "Number sense", text: "Children practice counting, patterns, shapes, and early mathematical language." }, { title: "Joyful formation", text: "Daily routines, songs, Scripture, and play support healthy habits." }] },
    ],
  },
  "academics/kindergarten": {
    id: "academics-kindergarten",
    slug: "academics/kindergarten",
    title: "Kindergarten",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Academics", title: "Kindergarten", body: "Kindergarten builds strong foundations in faith, reading, math, handwriting, language, history, science, music, art, and physical education.", className: "band-warm" },
      { id: "focus", type: "cards", title: "Kindergarten focus", cards: [{ title: "Reading readiness", text: "Phonics, language arts, handwriting, and story build literacy foundations." }, { title: "Ordered habits", text: "Students learn classroom routines, attention, perseverance, and kindness." }, { title: "Wonder", text: "History, science, music, and art invite curiosity about God's world." }] },
    ],
  },
  "academics/elementary": {
    id: "academics-elementary",
    slug: "academics/elementary",
    title: "Elementary",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Academics", title: "Elementary", body: "Elementary students grow through rich literature, math, history, geography, science, Latin, logic, music, art, physical education, and Christian formation.", className: "band-warm" },
      { id: "focus", type: "cards", title: "Elementary focus", cards: [{ title: "Liberal arts", text: "Students receive a broad, coherent education across disciplines." }, { title: "Memory and meaning", text: "Songs, chants, narration, discussion, and practice deepen understanding." }, { title: "Virtue and service", text: "Classroom culture supports humility, courage, diligence, and charity." }] },
    ],
  },
  admissions: {
    id: "admissions",
    slug: "admissions",
    title: "Admissions",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Admissions", title: "Start with a visit", body: "Your application process begins with a consultation visit and school tour with the Principal.", className: "band-warm" },
      { id: "process", type: "steps", title: "Admissions process", cards: [{ title: "Consultation Visit", text: "Call the office to schedule a visit with the Principal, share family goals, ask questions, and review school and parent roles." }, { title: "Tour & Shadow Day", text: "Tour the school after the consultation and, when appropriate, schedule a student shadow day with the class." }, { title: "Application Link", text: "If ZCA is a good fit for all parties, the Principal emails an application link and can discuss tuition assistance." }, { title: "Enrollment", text: "After acceptance, families receive the enrollment link and complete final enrollment steps." }], buttons: [{ label: "Schedule a Visit", href: "/contact", variant: "primary" }, { label: "Apply Online", href: "/admissions", variant: "outline" }] },
      { id: "tuition", type: "cards", eyebrow: "Tuition & Aid", title: "A conversation about fit and affordability", cards: [{ title: "Start with a visit", text: "ZCA begins with a conversation so families understand the school model, expectations, and next steps." }, { title: "Tuition assistance", text: "The Principal can discuss available assistance and fundraising resources during the admissions process." }, { title: "Faithful stewardship", text: "Supporters and partner programs help ZCA keep classical Christian education accessible." }] },
      { id: "policy", type: "text", title: "Non-discrimination policy", className: "band-warm", body: "Zion Classical Academy admits students of any race, color, nationality, or origin to the rights, privileges, programs, and activities generally made available to students. ZCA does not discriminate on the basis of race in its educational policies, scholarship program, or school programs." },
    ],
  },
  parents: {
    id: "parents",
    slug: "parents",
    title: "Parents",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Parents", title: "Resources for current families", body: "Quick access to family tools, calendars, forms, supplies, uniforms, service hours, and school information.", className: "band-warm" },
      { id: "resources", type: "cards", title: "Parent resources", cards: [{ title: "Parent Portal / TADS Educate", text: "Access grades, information, and family account tools." }, { title: "Lunch Menu", text: "View current lunch information." }, { title: "School Calendar", text: "See upcoming school events.", href: "/calendar" }, { title: "Forms & Handbooks", text: "Find important school documents." }, { title: "School Supplies", text: "Review classroom supply information." }, { title: "Uniforms", text: "Find uniform expectations and links." }, { title: "Family Partnership Hours", text: "Track volunteer and partnership opportunities." }, { title: "Student Services", text: "Connect with support resources." }] },
    ],
  },
  giving: {
    id: "giving",
    slug: "giving",
    title: "Giving",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Support ZCA", title: "Give toward faithful formation", body: "Financial gifts, fundraising programs, and volunteer service help Zion Classical Academy share God's message and keep classical Christian education accessible.", className: "band-warm" },
      { id: "give", type: "cta", title: "Give Online", body: "You may give by check today. Online giving is ready to connect to a hosted donation page when a PCI-compliant processor is selected.", buttons: [{ label: "Donate/Give", href: "/support", variant: "gold" }] },
      { id: "ways", type: "cards", title: "Ways to support", cards: [{ title: "Recurring giving", text: "Future hosted checkout can support monthly or annual recurring gifts after a payment processor is selected." }, { title: "Financial support", text: "Families and supporters can contact the Principal or ZCA Board to learn about current needs and ways to help." }, { title: "Volunteer support", text: "Parents and friends can serve through school events, classroom help, facilities, lunch, recess, field trips, and family partnership hours." }, { title: "RaiseRight", text: "ZCA participates in RaiseRight gift card fundraising." }, { title: "Box Tops, Casey's Rewards & Cartridges", text: "Everyday programs provide practical classroom support." }, { title: "FIRE / ACLEA", text: "The Faithfully Investing in Religious Education fundraiser through ACLEA helps defray tuition costs and support ZCA students." }] },
    ],
  },
  contact: {
    id: "contact",
    slug: "contact",
    title: "Contact",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Contact", title: "Connect with Zion Classical Academy", body: "We would be glad to answer questions, schedule a visit, or help your family learn more about ZCA.", className: "band-warm" },
      { id: "cta", type: "cta", title: "Schedule a Visit", body: "Start with a conversation and a school tour.", buttons: [{ label: "Contact Us", href: "mailto:info@zionclassical.org", variant: "gold" }] },
    ],
  },
  "christian-school-hastings-ne": {
    id: "christian-school-hastings-ne",
    slug: "christian-school-hastings-ne",
    title: "Christian School in Hastings, Nebraska",
    seoTitle: "Christian School in Hastings, NE",
    seoDescription: "Explore Zion Classical Academy, a classical Christian school serving Pre-K through 6th grade families in Hastings, Nebraska.",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Hastings, Nebraska", title: "A Christian school for faithful foundations", body: "Zion Classical Academy serves Hastings-area families who want a Christ-centered education with strong academics, joyful classrooms, and close family partnership.", className: "band-warm" },
      { id: "fit", type: "cards", title: "What families find at ZCA", cards: [{ title: "Classical Christian formation", text: "Students learn truth, wisdom, and virtue through Scripture, rich content, ordered habits, and meaningful practice." }, { title: "Pre-K through 6th grade", text: "ZCA focuses on the foundational years when attention, memory, language, number sense, and character are taking shape." }, { title: "Hastings community", text: "The school partners with families, churches, and local supporters to serve children in and around Hastings." }] },
      { id: "visit", type: "cta", title: "See ZCA in person", body: "The best next step is a school visit and conversation with the Principal.", buttons: [{ label: "Schedule a Visit", href: "/contact", variant: "gold" }] },
    ],
  },
  "classical-christian-school": {
    id: "classical-christian-school",
    slug: "classical-christian-school",
    title: "Classical Christian School",
    seoTitle: "Classical Christian Education in Hastings",
    seoDescription: "Learn how Zion Classical Academy uses classical Christian education to form young students in truth, wisdom, and virtue.",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Classical Christian Education", title: "A school model ordered toward wisdom", body: "Classical Christian education gives students more than facts. It invites them into Scripture, language, number, history, science, music, art, and habits that help them love what is true, good, and beautiful.", className: "band-warm" },
      { id: "model", type: "cards", title: "A formative approach", cards: [{ title: "Memory before mastery", text: "Young students are equipped with songs, chants, stories, Scripture, and facts that become lifelong furniture for the mind." }, { title: "Content with meaning", text: "Subjects are taught as connected parts of God's world, not isolated tasks." }, { title: "Virtue through practice", text: "Daily routines help students practice attention, humility, diligence, courage, and charity." }] },
      { id: "learn", type: "cta", title: "Explore the ZCA approach", body: "Visit the classical education page or schedule a conversation to learn how this model works in the classroom.", buttons: [{ label: "Read More", href: "/classical-education", variant: "outline" }, { label: "Schedule a Visit", href: "/contact", variant: "gold" }] },
    ],
  },
  "tuition-financial-aid": {
    id: "tuition-financial-aid",
    slug: "tuition-financial-aid",
    title: "Tuition and Financial Aid",
    seoTitle: "ZCA Tuition and Financial Aid",
    seoDescription: "Learn how prospective families can start a tuition and financial aid conversation with Zion Classical Academy.",
    status: "published",
    blocks: [
      { id: "header", type: "text", eyebrow: "Admissions", title: "Tuition questions are part of the admissions conversation", body: "Families considering ZCA are encouraged to schedule a visit and ask practical questions about tuition, assistance, fundraising programs, and the enrollment process.", className: "band-warm" },
      { id: "steps", type: "steps", title: "How to begin", cards: [{ title: "Schedule a Visit", text: "Meet with the Principal and learn about the school model." }, { title: "Discuss Needs", text: "Ask questions about tuition, assistance, and family expectations." }, { title: "Apply", text: "If ZCA is a good fit, complete the application and enrollment steps." }] },
      { id: "support", type: "cards", title: "Ways affordability is supported", cards: [{ title: "Tuition assistance", text: "Available assistance can be discussed during admissions." }, { title: "FIRE / ACLEA", text: "The Faithfully Investing in Religious Education fundraiser helps defray tuition costs." }, { title: "Supporters and giving", text: "Donors and fundraising programs help sustain access to classical Christian education." }] },
    ],
  },
};

fallbackPages.support = { ...fallbackPages.giving, id: "support", slug: "support", title: "Support" };

function mapPage(id: string, data: any): CmsPage {
  return {
    id,
    slug: String(data?.slug ?? id),
    title: String(data?.title ?? id),
    seoTitle: String(data?.seoTitle ?? ""),
    seoDescription: String(data?.seoDescription ?? ""),
    heroImageUrl: String(data?.heroImageUrl ?? ""),
    heroImageAlt: String(data?.heroImageAlt ?? ""),
    heroImageStoragePath: String(data?.heroImageStoragePath ?? ""),
    heroVideoUrl: String(data?.heroVideoUrl ?? ""),
    heroVideoStoragePath: String(data?.heroVideoStoragePath ?? ""),
    status: "published",
    blocks: Array.isArray(data?.blocks) ? data.blocks : [],
  };
}

export async function getCmsPage(slug: string, options: { useFallback?: boolean } = {}): Promise<CmsPage | null> {
  const useFallback = options.useFallback !== false;
  if (pageCache.has(slug)) {
    const cached = pageCache.get(slug) ?? null;
    if (cached || !useFallback) return cached;
  }
  if (!db) {
    const fallback = useFallback ? (fallbackPages[slug] ?? fallbackPages.home) : null;
    pageCache.set(slug, fallback);
    return fallback;
  }
  try {
    const snap = await getDoc(doc(db, "publicPages", slugToDocId(slug)));
    if (snap.exists()) {
      const page = mapPage(snap.id, snap.data());
      pageCache.set(slug, page);
      return page;
    }
  } catch (error) {
    console.warn("[public-web] Failed to load CMS page", slug, error);
  }
  const fallback = useFallback ? (fallbackPages[slug] ?? fallbackPages.home) : null;
  pageCache.set(slug, fallback);
  return fallback;
}

export async function getPublicTheme(): Promise<PublicSiteTheme> {
  if (themeCache) return themeCache;
  if (!db) return defaultTheme;
  try {
    const snap = await getDoc(doc(db, "publicSiteConfig", "theme"));
    if (snap.exists()) {
      themeCache = { ...defaultTheme, ...(snap.data() as Partial<PublicSiteTheme>) };
      return themeCache;
    }
  } catch (error) {
    console.warn("[public-web] Failed to load public theme", error);
  }
  themeCache = defaultTheme;
  return themeCache;
}

export async function getPublicChrome(): Promise<PublicChromeConfig> {
  if (chromeCache) return chromeCache;
  if (!db) return defaultChrome;
  try {
    const snap = await getDoc(doc(db, "publicSiteConfig", "chrome"));
    if (snap.exists()) {
      chromeCache = { ...defaultChrome, ...(snap.data() as Partial<PublicChromeConfig>) };
      return chromeCache;
    }
  } catch (error) {
    console.warn("[public-web] Failed to load public chrome", error);
  }
  chromeCache = defaultChrome;
  return chromeCache;
}

function slugFromPathname(pathname: string) {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/") return "home";
  if (normalized === "/news" || normalized === "/calendar" || normalized === "/staff") return null;
  if (normalized.startsWith("/forms/")) return null;
  return normalized.replace(/^\/+/, "");
}

export async function primePublicSiteBootstrap(pathname: string) {
  const slug = slugFromPathname(pathname);
  await Promise.all([
    getPublicTheme(),
    getPublicChrome(),
    slug ? getCmsPage(slug, { useFallback: false }).catch(() => null) : Promise.resolve(null),
  ]);
}

export async function getPublicNewsletters(): Promise<PublicNewsletter[]> {
  if (!db) return [];
  try {
    const [newsletterSnap, legacySnap] = await Promise.all([
      getDocs(query(collection(db, "publicNewsletters"), where("isPublished", "==", true), orderBy("publishedAt", "desc"), limit(25))),
      // Legacy fallback from the first scaffold. New archive records use publicNewsletters.
      getDocs(query(collection(db, "publicAnnouncements"), where("isPublished", "==", true), orderBy("publishedAt", "desc"), limit(25))),
    ]);
    const seen = new Set<string>();
    return [...newsletterSnap.docs, ...legacySnap.docs].filter((item) => {
      const key = item.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map((item) => {
      const data = item.data() as any;
      return {
        id: item.id,
        title: String(data?.title ?? ""),
        summary: String(data?.summary ?? ""),
        isPublished: data?.isPublished === true,
        publishedAt: data?.publishedAt?.toDate ? data.publishedAt.toDate().toISOString() : String(data?.publishedAt ?? ""),
        attachmentName: String(data?.attachmentName ?? ""),
        attachmentUrl: String(data?.attachmentUrl ?? ""),
        attachmentStoragePath: String(data?.attachmentStoragePath ?? ""),
      };
    }).sort((a, b) => String(b.publishedAt || "").localeCompare(String(a.publishedAt || ""))).slice(0, 25);
  } catch (error) {
    console.warn("[public-web] Failed to load public newsletters", error);
    return [];
  }
}
